import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { Direction } from 'radix-ui';

import type { Localized } from '@/lib/api';
import {
  directionOf, languageOf, t as resolveContent, secondary as resolveSecondary,
} from '@/lib/locale';
import { ar } from '@/lib/strings/ar';
import { en, type StringKey } from '@/lib/strings/en';

/**
 * i18n.tsx — the console's display language (Workstream H1).
 *
 * ### The distinction this whole workstream turns on
 *
 * **Display language belongs to the operator. Administration language belongs to
 * the instance. One must never resolve the other.**
 *
 * `lib/locale.ts` resolves bilingual *content* against a locale it is handed, and
 * says at length why it has no ambient "current language" to reach for. This file
 * introduces exactly one — the operator's — and it is safe only because of the
 * line it does not cross: `useT()` translates **console chrome**, strings we
 * wrote, and never decides what a candidate was administered.
 *
 * The harness is where that line is load-bearing, and it is why `useT` takes an
 * override. An operator whose profile is English opening an Arabic-pinned run
 * must see the run in Arabic — prompts, options, and the buttons the candidate
 * reads while answering. `RunAssessment` therefore calls `useT(administeredLocale)`
 * rather than `useT()`, and the surrounding console stays the operator's. There is
 * a test for it in `administered-locale.test.tsx`, because it is precisely the
 * kind of thing a later refactor "tidies away" into the ambient default.
 *
 * ### Why a hand-rolled catalogue rather than i18next
 *
 * `ar` is typed against `en`'s keys, so **a missing Arabic string is a build
 * failure**, not a runtime fallback nobody notices. That is the property worth
 * having in a console whose Arabic half will be reviewed by someone other than
 * the person who wrote the English — and it is the one thing a runtime i18n
 * library cannot give. Plurals, the real reason to reach for a library, are
 * handled by `Intl.PluralRules`, which is in the platform and knows Arabic's six
 * categories.
 */

export type Interpolations = Record<string, string | number>;

/** Translate a console string. Interpolates `{name}` placeholders. */
export type Translate = (key: StringKey, vars?: Interpolations) => string;

/** Base keys of a plural set — `'questions.selected'` from `'questions.selected.other'`. */
type PluralBase<K> = K extends `${infer B}.other` ? B : never;
export type PluralKey = PluralBase<StringKey>;

/** Translate a counted string, choosing the form the language actually needs. */
export type TranslatePlural = (key: PluralKey, count: number, vars?: Interpolations) => string;

const CATALOGUES: Record<'en' | 'ar', Record<string, string>> = { en, ar };

export const DEFAULT_LOCALE = 'en-AE';

function interpolate(template: string, vars?: Interpolations): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * Look a key up, falling back to English rather than rendering the key itself.
 *
 * The type system makes a missing Arabic key impossible, so this fallback is for
 * the case types cannot see: a plural category English does not have. A screen
 * showing `questions.selected.few` to an operator would be a defect they cannot
 * report usefully.
 */
export function translate(key: string, locale: string, vars?: Interpolations): string {
  const language = languageOf(locale);
  const found = CATALOGUES[language][key] ?? CATALOGUES.en[key];
  return found === undefined ? key : interpolate(found, vars);
}

/**
 * The plural form for `count` in this language.
 *
 * English needs two forms and Arabic needs six — `zero`, `one`, `two`, `few`,
 * `many`, `other` — which is the single strongest argument against writing
 * `count === 1 ? a : b` by hand anywhere in this codebase. `Intl.PluralRules`
 * ships with the runtime and already knows the rule.
 */
export function translatePlural(
  key: string,
  count: number,
  locale: string,
  vars?: Interpolations,
): string {
  const language = languageOf(locale);
  const category = new Intl.PluralRules(language).select(count);
  const catalogue = CATALOGUES[language];
  const template =
    catalogue[`${key}.${category}`] ??
    catalogue[`${key}.other`] ??
    CATALOGUES.en[`${key}.other`];

  return template === undefined ? key : interpolate(template, { ...vars, count });
}

const LocaleContext = createContext<string>(DEFAULT_LOCALE);

/**
 * Publishes the operator's language, and puts it on `<html>`.
 *
 * `lang` matters for screen readers and hyphenation; `dir` is what makes the
 * whole console mirror, because application code uses CSS logical properties
 * throughout (`10 §2`) and therefore needs telling the direction exactly once.
 * Radix's `DirectionProvider` is the same fact told to the component kit, so
 * dropdowns, popovers, selects, sheets and tooltips open toward the right edge.
 */
export function LocaleProvider({ locale, children }: { locale: string; children: ReactNode }) {
  useEffect(() => {
    const root = document.documentElement;
    root.lang = languageOf(locale);
    root.dir = directionOf(locale);
  }, [locale]);

  return (
    <LocaleContext.Provider value={locale}>
      <Direction.DirectionProvider dir={directionOf(locale)}>
        {children}
      </Direction.DirectionProvider>
    </LocaleContext.Provider>
  );
}

/** The operator's display locale. */
export function useLocale(): string {
  return useContext(LocaleContext);
}

/**
 * The console-string translator.
 *
 * Pass `override` **only** where the language is a property of the thing being
 * rendered rather than of the person reading it — which in this codebase means
 * the take-test harness and nothing else.
 */
export function useT(override?: string): Translate {
  const locale = useLocale();
  const active = override ?? locale;
  return useCallback((key, vars) => translate(key, active, vars), [active]);
}

/** As `useT`, for strings that carry a count. */
export function useTn(override?: string): TranslatePlural {
  const locale = useLocale();
  const active = override ?? locale;
  return useCallback((key, count, vars) => translatePlural(key, count, active, vars), [active]);
}

/**
 * Bilingual **content** in the operator's language — a module's name, an item's
 * prompt, a band label.
 *
 * Distinct from `useT` because the two fail differently and a reader should be
 * able to tell at a glance which they are looking at. A console string is missing
 * because we did not write it; a content string is missing because the catalogue
 * is half-translated, which is the normal case — so this falls back to the other
 * language rather than rendering blank (`locale.ts`).
 */
export function useContent(override?: string): (text: Localized | undefined) => string {
  const locale = useLocale();
  const active = override ?? locale;
  return useCallback((text) => (text ? resolveContent(text, active) : ''), [active]);
}

/**
 * The *other* language of a bilingual field, for the authoring screens that show
 * both — and empty when showing it would repeat the primary.
 *
 * This is what the catalogue screens were doing by hard-coding `.en` above `.ar`.
 * The difference is that the pair now follows the operator: an Arabic reader
 * gets Arabic first with English beneath, rather than the other way round.
 * Returns the text and its direction, because a secondary line in the opposite
 * script needs `dir` set on its own element — the surrounding page is running
 * the other way.
 */
export function useSecondary(): (text: Localized | undefined) => { text: string; dir: 'rtl' | 'ltr' } {
  const locale = useLocale();
  return useCallback(
    (text) => {
      const other = text ? resolveSecondary(text, locale) : '';
      // The secondary is by definition the language the operator did NOT pick.
      return { text: other, dir: directionOf(locale) === 'rtl' ? 'ltr' : 'rtl' };
    },
    [locale],
  );
}

/**
 * Label a value from a **closed enum** — a lifecycle status, an instance state,
 * a band, a QC flag.
 *
 * These are not free strings and not content: the contract fixes the set, and
 * every screen that renders one was previously printing the wire value
 * (`in_progress`) straight into a badge. One helper rather than a cast at each
 * call site, because the alternative is `t(\`state.${x}\` as StringKey)` spread
 * over a dozen files, which is a type assertion that a key exists — exactly the
 * check this catalogue is built to perform.
 *
 * **An unrecognised value renders as itself**, never as its key. A new enum
 * member added server-side should look like an untranslated value, not like a
 * broken screen — and `strings.test.ts` asserts the sets are complete, so this
 * fallback is a safety net rather than the plan.
 */
export function useLabel(): (prefix: string, value: string | undefined | null) => string {
  const locale = useLocale();
  return useCallback(
    (prefix, value) => {
      if (!value) return '';
      const key = `${prefix}.${value}`;
      return key in en ? translate(key, locale) : value;
    },
    [locale],
  );
}

/**
 * Translate a key the catalogue **may not have**, and say so rather than guess.
 *
 * `useT` is typed to `StringKey`, which is exactly right for chrome we wrote: a
 * typo is a build failure. It cannot help where the key arrives at RUNTIME, from
 * an open registry — a headline template id emitted by a plugin (N5) is the
 * case, and a plugin registered tomorrow will emit one this catalogue has never
 * seen. Returning `null` is what lets the caller fall back to something a human
 * wrote instead of printing `headline.riasec.code_band` at a counsellor.
 *
 * `useLabel` and `useOptionalLabel` are this function with a prefix composed in;
 * they stay as the ergonomic form for the closed sets, which are the common
 * case and where the `prefix + value` split is meaningful.
 */
export function useOptionalT(): (key: string, vars?: Interpolations) => string | null {
  const locale = useLocale();
  return useCallback(
    (key, vars) => (key in en ? translate(key, locale, vars) : null),
    [locale],
  );
}

/**
 * `useLabel`, but honest about a miss.
 *
 * `useLabel` renders an unrecognised value as itself, which is the right default
 * for a badge: a new enum member should look untranslated rather than broken.
 * The cost is that its caller cannot tell "translated" from "echoed back" —
 * and for the closed sets that matter here it genuinely cannot, because
 * `band.High` translates to the string `High`.
 *
 * A caller that must *decide* on the answer needs the distinction. The result
 * screen's narrative resolution is the case: a key it cannot resolve is dropped
 * rather than printed, and identity comparison would have dropped every English
 * band label there is.
 */
export function useOptionalLabel(): (
  prefix: string,
  value: string | undefined | null,
) => string | null {
  const optional = useOptionalT();
  return useCallback(
    (prefix, value) => (value ? optional(`${prefix}.${value}`) : null),
    [optional],
  );
}

/**
 * The display name of a scoring dimension.
 *
 * Three sources, in order, and the order is the point:
 *
 * 1. the `dimension.*` catalogue, which is the only one that can be Arabic;
 * 2. the label the envelope carried, which is English but is at least a human
 *    phrase an author wrote — a module ETCC adds tomorrow will land here;
 * 3. the raw key, so a chart axis never renders blank.
 *
 * Step 2 is what makes this safe to apply to an OPEN registry. `useLabel` alone
 * would fall back from an unknown key straight to the key itself, so a new
 * module's dimensions would read `emotionalStability` rather than
 * `Emotional Stability` — which is exactly what the Dashboard and Analytics
 * charts were doing, since the results aggregate carries no label at all.
 */
export function useDimensionLabel(): (
  key: string | undefined | null,
  fallback?: Localized,
) => string {
  const label = useLabel();
  const content = useContent();
  return useCallback(
    (key, fallback) => {
      if (!key) return '';
      const translated = label('dimension', key);
      if (translated !== key) return translated;
      return content(fallback) || key;
    },
    [label, content],
  );
}

/* ------------------------------------------------------------- numerals ---
 *
 * **WESTERN DIGITS (0123), IN BOTH LANGUAGES — a decision, not a default.**
 *
 * `plan-beta` §8 H3 flags this as a product question and names the failure mode
 * exactly: *"mixing them within one table"*. Arabic can be written with
 * Arabic-Indic digits (٠١٢٣) or Western ones, and this console has to pick,
 * because a score, a config version and a row count regularly appear in the same
 * row — one of them rendered `٧٨` beside another rendered `v3` is harder to scan
 * than either convention applied consistently.
 *
 * **Decided: Western throughout** (ETCC, 2026-07-31). Every score, version
 * string, identifier and count in the platform is already Western, and UAE
 * government digital services predominantly are too.
 *
 * `ar-AE` happens to resolve to `latn` in ICU anyway, so this pins behaviour
 * that is *currently* accidental. That is the point: `ar-EG` resolves to
 * `arab`, and a locale added later must not silently change how a score reads.
 * The `-u-nu-latn` extension says so in the one place it can be enforced.
 */
export const NUMERALS = 'latn';

const withNumerals = (locale: string): string => `${locale}-u-nu-${NUMERALS}`;

/**
 * A date and time in the operator's locale, with the numerals pinned.
 *
 * Call sites used to use bare `toLocaleString()`, which reads
 * `navigator.language` — neither the operator's choice nor stable between their
 * machines, so two people could see one timestamp two ways.
 */
export function useDateTime(): (iso: string | null | undefined) => string {
  const locale = useLocale();
  return useCallback(
    (iso) => (iso ? new Date(iso).toLocaleString(withNumerals(locale)) : ''),
    [locale],
  );
}

/** A number in the operator's locale, with the numerals pinned. */
export function useNumber(): (value: number) => string {
  const locale = useLocale();
  return useCallback((value) => value.toLocaleString(withNumerals(locale)), [locale]);
}

/**
 * A percentage, with the sign on the side the language puts it.
 *
 * **Arabic leads with the sign (`%10.5`), English trails it (`10.5%`)** —
 * decided 2026-08-02, and the reason it needed deciding is that S6 photographed
 * the console disagreeing with itself: one dashboard tile read `75%` while the
 * two beneath it read `%10.5`, in the same view.
 *
 * Nothing was formatting anything. Every call site was building `${n}%` by hand,
 * and the browser's bidi algorithm then placed the sign — a NEUTRAL character —
 * according to whatever ran next to it. Beside a bare number inside an RTL
 * paragraph it flipped to the left; inside its own element it stayed put. So the
 * two spellings were the same string, resolved twice.
 *
 * Composing the string correctly is NOT enough on its own, and the first attempt
 * at this proved it: with the sign merely placed and the direction left to the
 * page, a dashboard tile rendered `%62` while the hint beneath it rendered
 * `12.5%` — the same helper, re-ordered by whatever ran beside each one.
 *
 * So the result is wrapped in a **bidi isolate** (U+2066 … U+2069) and is
 * therefore self-contained: it renders the same inside an Arabic paragraph, an
 * English one, a chart label or a string joined with ` · `. Requiring callers to
 * remember `dir="ltr"` was the same bug waiting on someone's memory — and half
 * the call sites are plain strings with nowhere to hang an attribute.
 */
const LRI = '⁦';
const PDI = '⁩';

export function usePercent(): (value: number, fractionDigits?: number) => string {
  const locale = useLocale();
  return useCallback(
    (value, fractionDigits) => {
      const n = value.toLocaleString(withNumerals(locale), {
        minimumFractionDigits: fractionDigits ?? 0,
        maximumFractionDigits: fractionDigits ?? 1,
      });
      return `${LRI}${languageOf(locale) === 'ar' ? `%${n}` : `${n}%`}${PDI}`;
    },
    [locale],
  );
}

/** The percent helper's isolate marks, for tests and for anywhere that must compare. */
export const stripBidi = (s: string): string => s.replace(/[⁦-⁩]/g, '');

/** Direction of the operator's locale, for the few places that must branch on it. */
export function useDirection(): 'rtl' | 'ltr' {
  const locale = useLocale();
  return useMemo(() => directionOf(locale), [locale]);
}
